<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { MenuItems, Portal } from '@headlessui/vue'
import { autoPlacement, autoUpdate, computePosition, flip, offset as offsetPlugin, shift, size } from '@floating-ui/vue'
import type { ComputePositionConfig, Placement } from '@floating-ui/vue'
import type { DropdownProvider } from '../../types/dropdown'
import { DTDropdownProvider } from '../../keys'
import { classArray } from '../../utils/class'
import type { ComplexClassName } from '../../types/common'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<{
  placement?: Placement | 'auto'
  offset?: number
  menuClass?: ComplexClassName
  contentSize?: 'button-width'
}>(), {
  placement: 'bottom',
  offset: 12,
  menuClass: '',
})

const menuItemsClass = computed(() => {
  const cls = [
    'min-w-content',
    'border',
    'p-1.5',
    'border-gray-200',
    'rounded-xl',
    'shadow-lg',
    'bg-white',
    'overflow-hidden',
    'focus:outline-none',
    'text-left',
  ]

  return cls.concat(classArray(props.menuClass))
})

const dropdownProvider = inject<DropdownProvider>(DTDropdownProvider)

const trigger = computed(() => dropdownProvider?.getTrigger())
const items = ref<HTMLElement>()

async function calculatePosition() {
  const options: ComputePositionConfig = {
    middleware: [
      offsetPlugin(props.offset),
      shift({ padding: 4 }),
      props.contentSize === 'button-width'
        ? size({ apply({ rects }) {
          if (items.value)
            items.value.style.width = `${rects.reference.width}px`
        } })
        : size(),
    ],
  }

  if (props.placement !== 'auto') {
    options.placement = props.placement
    options.middleware?.unshift(flip())
  }
  else {
    options.middleware?.unshift(autoPlacement())
  }

  const { x, y } = await computePosition(
    trigger.value!,
    items.value!,
    options,
  )

  if (!items.value)
    return

  Object.assign(items.value!.style, {
    left: `${x}px`,
    top: `${y}px`,
  })
}

let cleanup: () => void = () => {}
watch(trigger, (trigger) => {
  if (trigger) {
    nextTick(() => {
      cleanup = autoUpdate(trigger, items.value!, calculatePosition)
    })
  }
})
onBeforeUnmount(() => cleanup())
</script>

<template>
  <Portal>
    <div
      ref="items"
      class="absolute z-10 w-max max-w-[100vw]"
      v-bind="$attrs"
    >
      <transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <MenuItems :class="menuItemsClass">
          <slot />
        </MenuItems>
      </transition>
    </div>
  </Portal>
</template>
